/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { defaultsDeep, omit } from 'lodash'

import { AjaxRequestError } from '@modules/ui-types'
import type { AsyncRequestError } from '@modules/ui-types'
import type { DeepPartial } from '@modules/ts-essentials'

export type AjaxConfig = {
  headers?: Record<string, string | number>
  settings?: {
    json?: boolean
    binary?: boolean
    request?: unknown
    response?: {
      fullStatus?: boolean
    }
    maxResponseSize?: number
    maxCompressedResponseSize?: number
  }
  includeHeaders?: boolean
}

type AjaxJsonOrTextResult = {
  body?: any
  headers?: Record<string, any>
  status?: number
  statusText?: string
  contentType?: string | null
}

export interface AjaxBlobResult {
  blobUrl?: string
  blob?: Blob
}

export type AjaxResult = AjaxJsonOrTextResult & AjaxBlobResult

const defaultRequestConfig: DeepPartial<AjaxConfig> = {
  headers: {
    Accept: `application/json`,
  },
}

// @ts-ignore: `webkitURL` might or not be a thing
const windowUrl = window.URL || window.webkitURL

const hasApiError = (body: any) => (body.ok === false && body.error) || body?.error?.reason

function ajaxGet(url: string, config: AjaxConfig = {}) {
  return fetch(url, extendConfig(config)).then(extractResponse(config))
}

function ajaxPost(url: string, body?, config: AjaxConfig = {}) {
  return fetch(url, {
    method: `post`,
    body: getRequestBody(body, config),
    ...extendConfig(config),
  }).then(extractResponse(config))
}

function ajaxPut(url: string, body?, config: AjaxConfig = {}) {
  return fetch(url, {
    method: `put`,
    body: getRequestBody(body, config),
    ...extendConfig(config),
  }).then(extractResponse(config))
}

function ajaxPatch(url: string, body?, config: AjaxConfig = {}) {
  return fetch(url, {
    method: `PATCH`,
    body: getRequestBody(body, config),
    ...extendConfig(config),
  }).then(extractResponse(config))
}

function ajaxDel(url: string, body?, config: AjaxConfig = {}) {
  return fetch(url, {
    method: `delete`,
    body: getRequestBody(body, config),
    ...extendConfig(config),
  }).then(extractResponse(config))
}

function getRequestBody(body, config: AjaxConfig) {
  if (body == null) {
    return null
  }

  const isJson = config.settings?.json ?? true

  if (isJson !== false) {
    if (config.headers == null) {
      config.headers = {}
    }

    config.headers['Content-Type'] = `application/json`
    return JSON.stringify(body)
  }

  return body
}

function extendConfig(config: AjaxConfig) {
  const requestSettings = config.settings?.request ?? {}
  const pureConfig = omit(config, `settings`)
  const appliedDefaults = defaultsDeep(pureConfig, defaultRequestConfig)
  const appliedSettings = defaultsDeep(requestSettings, appliedDefaults)
  return appliedSettings
}

function filterHeaders(headers) {
  const filteredHeaders: Record<string, unknown> = {}

  const date = headers.get(`date`)

  if (date) {
    filteredHeaders.date = date
  }

  const etag = headers.get(`etag`)

  if (etag) {
    filteredHeaders.etag = etag
  }

  const version = headers.get(`x-cloud-resource-version`)

  if (version) {
    filteredHeaders[`x-cloud-resource-version`] = parseInt(version, 10) || version
    filteredHeaders[`x-cloud-resource-version.raw`] = version
  }

  const created = headers.get(`x-cloud-resource-created`)

  if (created) {
    filteredHeaders[`x-cloud-resource-created`] = new Date(created)
  }

  const lastModified = headers.get(`x-cloud-resource-last-modified`)

  if (lastModified) {
    filteredHeaders[`x-cloud-resource-last-modified`] = new Date(lastModified)
  }

  const uiTag = headers.get(`x-ui-tag`)

  if (uiTag) {
    filteredHeaders[`x-ui-tag`] = uiTag
  }

  return filteredHeaders
}

function extractResponse(config: AjaxConfig) {
  return (response: Response): Promise<AjaxResult> => {
    if (response.status === 204) {
      return Promise.resolve({})
    }

    const wantBlob = config.settings?.binary ?? false
    const includeStatus = config.settings?.response?.fullStatus || false
    const maxResponseSize = config.settings?.maxResponseSize
    const maxCompressedResponseSize = config.settings?.maxCompressedResponseSize
    const contentType = response.headers.get(`content-type`)
    const isNotJson = !contentType || !contentType.startsWith(`application/json`)
    const isText = contentType && contentType.startsWith(`text/plain`)
    const contentEncoding = response.headers.get('content-encoding')
    const isCompressed =
      contentEncoding &&
      (contentEncoding.includes('gzip') ||
        contentEncoding.includes('deflate') ||
        contentEncoding.includes('br'))

    // Check Content-Length header for compressed response size limit
    if (maxCompressedResponseSize && isCompressed) {
      const contentLength = response.headers.get('content-length')

      if (contentLength && parseInt(contentLength, 10) > maxCompressedResponseSize) {
        throw createError(
          `Compressed response size ${contentLength} bytes exceeds maximum allowed compressed size of ${maxCompressedResponseSize} bytes`,
          null,
        )
      }
    }

    // Check Content-Length header for uncompressed response size limit (for non-compressed responses)
    if (maxResponseSize && !isCompressed) {
      const contentLength = response.headers.get('content-length')

      if (contentLength && parseInt(contentLength, 10) > maxResponseSize) {
        throw createError(
          `Response size ${contentLength} bytes exceeds maximum allowed size of ${maxResponseSize} bytes`,
          null,
        )
      }
    }

    if (wantBlob && isNotJson) {
      return response.blob().then((blob) => {
        statusCheck(blob)

        // Check actual blob size if maxResponseSize is specified
        if (maxResponseSize && blob.size > maxResponseSize) {
          throw createError(
            `Response size ${blob.size} bytes exceeds maximum allowed size of ${maxResponseSize} bytes`,
            null,
          )
        }

        return {
          blobUrl: windowUrl.createObjectURL(blob),
          blob,
        }
      })
    }

    return (isText ? response.text() : response.json()).then(
      (body) => {
        statusCheck(body)

        // Check actual response size if maxResponseSize is specified
        if (maxResponseSize && body && typeof body === 'string') {
          const responseSize = new Blob([body]).size

          if (responseSize > maxResponseSize) {
            throw createError(
              `Response size ${responseSize} bytes exceeds maximum allowed size of ${maxResponseSize} bytes`,
              null,
            )
          }
        } else if (maxResponseSize && body && typeof body === 'object') {
          const responseSize = new Blob([JSON.stringify(body)]).size

          if (responseSize > maxResponseSize) {
            throw createError(
              `Response size ${responseSize} bytes exceeds maximum allowed size of ${maxResponseSize} bytes`,
              null,
            )
          }
        }

        const ajaxResult: AjaxJsonOrTextResult = {
          body,
        }

        if (config.includeHeaders) {
          ajaxResult.headers = filterHeaders(response.headers)
        }

        if (includeStatus) {
          ajaxResult.status = response.status
          ajaxResult.statusText = response.statusText
          ajaxResult.contentType = contentType
        }

        return ajaxResult
      },
      () => {
        throw createError(response.statusText, null)
      },
    )

    function statusCheck(body) {
      if (hasApiError(body)) {
        throw createError(getApiErrorMessage(body), body)
      }

      if (response.status < 200 || response.status >= 300) {
        throw createError(response.statusText, body)
      }
    }

    function createError(message, body) {
      return new AjaxRequestError(message, { response, body })
    }

    function getApiErrorMessage(body) {
      const hasError = typeof body.error === `string`

      if (hasError) {
        return body.error
      }

      const hasErrorReason = typeof body?.error?.reason === `string`

      if (hasErrorReason) {
        return body?.error?.reason
      }

      return `Internal Server Error`
    }
  }
}

export function asAjaxRequestError(error?: AsyncRequestError): AjaxRequestError | undefined {
  return error instanceof AjaxRequestError ? error : undefined
}

function isAjaxTimeoutError(error?: AsyncRequestError): boolean {
  return getResponseStatus(error) === 504
}

export function isAjaxNotFound(error?: AsyncRequestError): boolean {
  return getResponseStatus(error) === 404
}

export function getResponseStatus(error?: AsyncRequestError): number | undefined {
  return error instanceof AjaxRequestError ? error.response.status : undefined
}

export {
  ajaxGet as get,
  ajaxPatch as patch,
  ajaxPost as post,
  ajaxPut as put,
  ajaxDel as del,
  isAjaxTimeoutError,
}
